import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Skeleton,
  TableCell,
  TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ClassNames } from '@emotion/react';
import PropTypes from 'prop-types';
import CellSelector from '../Cells/CellSelector';
import CellViewer from '../Cells/CellViewer';
import QueryAdder from '../QueryAdder/QueryAdder';
import { createContext, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import ToolTip from '@mui/material/Tooltip';
import { API, TrackerDataType } from '../../shared/constants/constants';
import { useLocation } from 'react-router-dom';
import { processCellsForAutoComplete } from '../../shared/functions/autoCompleteProcessing';
import AddLinkIcon from '@mui/icons-material/AddLink';
import UrlDialog from '../ServiceLog/UrlDialog';
import { useDispatch, useSelector } from 'react-redux';
import { openSnackbar } from '../AppSnackbar/snackbarSlice';
import { ContentLogKeys, EngagementKeys, IssueKeys, ServiceKeys } from '../../shared/constants/IssueAndServiceKeys';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import EditCommsMaterialDialog from '../Cells/EditCommsMaterialDialog';
import EditCollapsedLiveLinkDialog from '../Cells/EditCollapsedLiveLinkDialog';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { normalizeServiceId } from '../../shared/utils/serviceLogNormalization';
import { getQueryAdderUiPalette } from '../../shared/constants/queryAdderPalette';
import { queryAdderTooltipProps, queryAdderTooltipTitleSx } from '../QueryAdder/queryAdderPopupStyles';
import { neutralPalette } from '../../theme/palette';

const hiddenColsMap = {
  MediaInteraction: [
    {
      name: IssueKeys.dueDate,
      sx: {
        flex: '0 1 6%',
      },
    },
    {
      name: IssueKeys.contactMethod,
      sx: {
        flex: '0 1 10%',
      },
    },
    {
      name: IssueKeys.dept,
      sx: {
        flex: '0 1 8%',
      },
    },
    {
      name: IssueKeys.keyMessaging,
      sx: {
        flex: '0 1 27%',
      },
    },
    {
      name: IssueKeys.response,
      sx: {
        flex: '0 1 27%',
      },
    },
    {
      name: IssueKeys.linkedService,
      sx: {
        flex: '0 1 12%',
      },
    },
    {
      name: IssueKeys.file,
      sx: {
        flex: '0 1 10%',
      },
    },
  ],
  ServiceLog: [
    {
      name: ServiceKeys.dept,
      sx: {
        flex: '0 1 10%',
      },
    },
    {
      name: ServiceKeys.keyMessaging,
      sx: {
        flex: '0 1 28%',
      },
    },
    {
      name: ServiceKeys.actions,
      sx: {
        flex: '0 1 28%',
      },
    },
    {
      name: ServiceKeys.linkedInteraction,
      sx: {
        flex: '0 1 22%',
      },
    },
    {
      name: ServiceKeys.file,
      sx: {
        flex: '0 1 14%',
      },
    },
  ],
  EngagementLog: [
    {
      name: EngagementKeys.dueDate,
      sx: {
        flex: '0 1 6%',
      },
    },
    {
      name: EngagementKeys.contactMethod,
      sx: {
        flex: '0 1 10%',
      },
    },
    {
      name: EngagementKeys.dept,
      sx: {
        flex: '0 1 8%',
      },
    },
    {
      name: EngagementKeys.notes,
      sx: {
        flex: '0 1 23%',
      },
    },
    {
      name: EngagementKeys.background,
      sx: {
        flex: '0 1 23%',
      },
    },
    {
      name: EngagementKeys.linkedService,
      sx: {
        flex: '0 1 12%',
      },
    },
    {
      name: EngagementKeys.file,
      sx: {
        flex: '0 1 20%',
      },
    },
  ],
  ContentLog: [
    {
      name: ContentLogKeys.type,
      sx: {
        flex: '1 1 10%',
      },
    },
    {
      name: ContentLogKeys.otherNotes,
      sx: {
        flex: '1 1 10%',
      },
    },
    {
      name: ContentLogKeys.content,
      sx: {
        flex: '1 1 26%',
      },
    },
    {
      name: ContentLogKeys.background,
      sx: {
        flex: '1 1 26%',
      },
    },
    {
      name: ContentLogKeys.linkedService,
      sx: {
        flex: '1 1 10%',
      },
    },
    {
      name: ContentLogKeys.file,
      sx: {
        flex: '1 1 15%',
      },
    },
    {
      name: ContentLogKeys.liveLink,
      sx: {
        flex: '1 1 10%',
      },
    },
  ],
};

const loadingDetailLabelsByType = {
  [TrackerDataType.SERVICE]: 'service',
  [TrackerDataType.ENGAGEMENT]: 'engagement',
  [TrackerDataType.CONTENT_LOG]: 'content log',
};

const getLoadingColumnCount = (type) => {
  const mappedCols = hiddenColsMap[type]?.length ?? 0;
  if (mappedCols <= 2) {
    return 2;
  }
  if (mappedCols >= 6) {
    return 3;
  }
  return 3;
};

const getLoadingColumnKeys = (type) =>
  Array.from({ length: getLoadingColumnCount(type) }, (_unused, index) => `loading-col-${type}-${index + 1}`);

const getHiddenCellDisplayName = (name) => {
  if (name === IssueKeys.contactMethod || name === EngagementKeys.contactMethod) {
    return 'Contact Method';
  }
  if (name === ContentLogKeys.linkedService) {
    return 'Linked Service';
  }
  return name;
};

const getHiddenCardTitle = ({
  displayName,
  isContentLiveLinkCell,
  userTenantHasContentLogAddOn,
  userTenantHasEngagementAddOn,
}) => {
  let hiddenCardTitle = displayName;
  if (isContentLiveLinkCell) {
    hiddenCardTitle = ContentLogKeys.liveLink;
  }
  if (displayName === ServiceKeys.linkedInteraction) {
    hiddenCardTitle = 'Linked Logs';
  }
  return hiddenCardTitle;
};

const getHiddenCellFlags = ({ name, type, hasLiveLink, hiddenCardTitle }) => {
  const isMediaStoryLinkCell = name === IssueKeys.file && type === TrackerDataType.MEDIA_INTERACTION;
  const isContentLiveLinkCell = name === ContentLogKeys.liveLink && type === TrackerDataType.CONTENT_LOG;
  const isFileCell =
    name === ServiceKeys.file ||
    name === IssueKeys.file ||
    name === EngagementKeys.file ||
    name === ContentLogKeys.file;
  const isCommsMaterialFileCell =
    name === ServiceKeys.file || name === EngagementKeys.file || name === ContentLogKeys.file;
  const shouldShowFileUpload =
    name !== ContentLogKeys.liveLink &&
    (type === TrackerDataType.SERVICE || type === TrackerDataType.ENGAGEMENT || type === TrackerDataType.CONTENT_LOG);

  return {
    isMediaStoryLinkCell,
    isContentLiveLinkCell,
    isFileCell,
    isLinkedLogsHeader: hiddenCardTitle === 'Linked Logs',
    isLinkedServiceHeader: hiddenCardTitle === 'Linked Service',
    shouldRenderCommsActionsInDropzone: isCommsMaterialFileCell && shouldShowFileUpload,
    shouldShowFileUpload,
    shouldShowHeaderLinkAction: isMediaStoryLinkCell || (isContentLiveLinkCell && !hasLiveLink),
    shouldShowLiveLinkUpload: isContentLiveLinkCell,
  };
};

const renderIconOnlyUrlUploadAction = ({ setFormOpen, accentColor }) => (
  <Box sx={{ width: 'auto', maxWidth: 'none', flexShrink: 0 }}>
    <UrlUploadButton setFormOpen={setFormOpen} accentColor={accentColor} useIconOnly={true} />
  </Box>
);

const getFileHiddenCardActions = ({
  isMediaStoryLinkCell,
  shouldShowHeaderLinkAction,
  shouldRenderCommsActionsInDropzone,
  shouldShowFileUpload,
  file,
  onChange,
  name,
  setIsUrlFormOpen,
  accentColor,
}) => {
  if (isMediaStoryLinkCell) {
    return shouldShowHeaderLinkAction
      ? renderIconOnlyUrlUploadAction({ setFormOpen: setIsUrlFormOpen, accentColor })
      : null;
  }

  if (shouldRenderCommsActionsInDropzone) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {shouldShowFileUpload ? (
          <FileUploadButton file={file} onChange={onChange(name)} accentColor={accentColor} useIconOnly={true} />
        ) : null}
        <UrlUploadButton setFormOpen={setIsUrlFormOpen} accentColor={accentColor} useIconOnly={true} />
      </Box>
    );
  }

  return (
    <>
      {shouldShowFileUpload ? <FileUploadButton file={file} onChange={onChange(name)} /> : null}
      <UrlUploadButton setFormOpen={setIsUrlFormOpen} />
    </>
  );
};

const getLiveLinkHiddenCardActions = ({
  shouldShowHeaderLinkAction,
  hasLiveLink,
  cells,
  handleOpenEditModal,
  accentColor,
}) => {
  if (!shouldShowHeaderLinkAction) return null;

  return renderIconOnlyUrlUploadAction({
    setFormOpen: () =>
      handleOpenEditModal(hasLiveLink ? cells?.[ContentLogKeys.liveLink] : null, ContentLogKeys.liveLink),
    accentColor,
  });
};

const getHiddenCardActions = ({ isFileCell, shouldShowLiveLinkUpload, fileActionProps, liveLinkActionProps }) => {
  if (isFileCell) {
    return getFileHiddenCardActions(fileActionProps);
  }
  if (shouldShowLiveLinkUpload) {
    return getLiveLinkHiddenCardActions(liveLinkActionProps);
  }
  return null;
};

const uploadPathPrefixesByType = {
  [TrackerDataType.SERVICE]: 'service',
  [TrackerDataType.ENGAGEMENT]: 'engagement',
  [TrackerDataType.CONTENT_LOG]: 'content-log',
};

const monthlyUploadQueryKeysByType = {
  [TrackerDataType.SERVICE]: 'monthlyServices',
  [TrackerDataType.ENGAGEMENT]: 'monthlyEngagements',
  [TrackerDataType.CONTENT_LOG]: 'monthlyContentLogs',
};

const getCommsActionButtonSx = (accentColor = '#2F7FD9') => ({
  textTransform: 'none',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
  px: 1.4,
  py: 0.4,
  minHeight: 28,
  minWidth: 'auto',
  backgroundColor: '#FFFFFF',
  borderColor: accentColor,
  color: accentColor,
  '&:hover': {
    borderColor: accentColor,
    backgroundColor: '#F5FAFF',
  },
});

const getCommsIconButtonSx = (accentColor = '#2F7FD9') => ({
  width: 26,
  height: 26,
  borderRadius: '7px',
  border: `1px solid ${accentColor}`,
  backgroundColor: '#FFFFFF',
  color: accentColor,
  p: 0,
  flexShrink: 0,
  '&:hover': {
    borderColor: accentColor,
    backgroundColor: '#F5FAFF',
  },
});

const FileUploadButton = ({ file, onChange, accentColor, useIconOnly = false }) => {
  if (useIconOnly) {
    return (
      <>
        <input type="file" multiple id="file" ref={file} onChange={onChange} style={{ display: 'none' }} />
        <ToolTip
          title={
            <Box component="span" sx={queryAdderTooltipTitleSx}>
              Add file(s)
            </Box>
          }
          {...queryAdderTooltipProps}
        >
          <label htmlFor="file" data-cy="file-upload-button">
            <IconButton onClick={() => file.current.click()} size="small" sx={getCommsIconButtonSx(accentColor)}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </label>
        </ToolTip>
      </>
    );
  }

  return (
    <>
      <input type="file" multiple id="file" ref={file} onChange={onChange} style={{ display: 'none' }} />
      <ToolTip
        title={
          <Box component="span" sx={queryAdderTooltipTitleSx}>
            Add file(s)
          </Box>
        }
        {...queryAdderTooltipProps}
      >
        <label htmlFor="file" data-cy="file-upload-button">
          <IconButton onClick={() => file.current.click()} size="large">
            <AddIcon style={{ color: 'white' }} />
          </IconButton>
        </label>
      </ToolTip>
    </>
  );
};

const UrlUploadButton = ({ setFormOpen, accentColor, useCommsStyle = false, useIconOnly = false }) => {
  if (useIconOnly) {
    return (
      <ToolTip
        title={
          <Box component="span" sx={queryAdderTooltipTitleSx}>
            Add link to web-hosted file
          </Box>
        }
        {...queryAdderTooltipProps}
      >
        <IconButton
          onClick={() => setFormOpen(true)}
          size="small"
          sx={getCommsIconButtonSx(accentColor)}
          data-cy="url-upload-button"
        >
          <AddLinkIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </ToolTip>
    );
  }

  if (useCommsStyle) {
    return (
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddLinkIcon />}
        onClick={() => setFormOpen(true)}
        sx={getCommsActionButtonSx(accentColor)}
        data-cy="url-upload-button"
      >
        Add Link
      </Button>
    );
  }

  return (
    <>
      <ToolTip
        title={
          <Box component="span" sx={queryAdderTooltipTitleSx}>
            Add link to web-hosted file
          </Box>
        }
        {...queryAdderTooltipProps}
      >
        <IconButton onClick={() => setFormOpen(true)} size="large" data-cy="url-upload-button">
          <AddLinkIcon style={{ color: 'white' }} />
        </IconButton>
      </ToolTip>
    </>
  );
};

FileUploadButton.propTypes = {
  file: PropTypes.shape({ current: PropTypes.any }),
  onChange: PropTypes.func.isRequired,
  accentColor: PropTypes.string,
  useCommsStyle: PropTypes.bool,
  useIconOnly: PropTypes.bool,
};

UrlUploadButton.propTypes = {
  setFormOpen: PropTypes.func.isRequired,
  accentColor: PropTypes.string,
  useCommsStyle: PropTypes.bool,
  useIconOnly: PropTypes.bool,
};

export const FileProgressContext = createContext();

const isServicePath = (pathPrefix) => pathPrefix === 'service';

const TrackerCollapsedRow = ({
  cells,
  open,
  colSpan,
  dropdownTable,
  isEditMode,
  onEdit,
  type,
  linkedEventHandler,
  secondaryLinkedEventHandler,
  tertiaryLinkedEventHandler,
  id,
  onOpenFullEntryEditor,
  isLoadingDetail = false,
}) => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const user = useSelector((state) => state.user);
  const userTenantHasEngagementAddOn = user.addonControls[AddOnTypes.engagementLog];
  const userTenantHasContentLogAddOn = user.addonControls[AddOnTypes.contentLog];
  const loadingDetailLabel = loadingDetailLabelsByType[type] ?? 'interaction';

  const file = useRef(null);
  const [fileProgress, setFileProgress] = useState();

  const [isUrlFormOpen, setIsUrlFormOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentFileToEdit, setCurrentFileToEdit] = useState(null);
  const [currentEditColumn, setCurrentEditColumn] = useState(null);

  const [isDragActive, setDragActive] = useState(false);

  const uploadFilesMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, files, onProgress }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        if (!normalizedId) {
          throw new Error('invalid service file upload payload');
        }
        const headers = { 'content-type': 'multipart/form-data' };
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('file', file);
        });

        return API.post(`/${pathPrefix}/${normalizedId}/upload`, formData, {
          headers: headers,
          onUploadProgress: onProgress,
        });
      }
      const headers = { 'content-type': 'multipart/form-data' };
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('file', file);
      });

      return API.post(`/${pathPrefix}/${id}/upload`, formData, {
        headers: headers,
        onUploadProgress: onProgress,
      });
    },
  });

  const uploadUrlMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, dialogValue }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        if (!normalizedId) {
          throw new Error('invalid service url payload');
        }
        return API.post(`/${pathPrefix}/${normalizedId}/url`, dialogValue);
      }
      return API.post(`/${pathPrefix}/${id}/url`, {
        url: dialogValue.url,
        name: dialogValue.name,
        description: dialogValue.description ?? '',
      });
    },
  });

  const updateFileMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, updatedFile }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        const fileId = normalizeServiceId(updatedFile?._id);
        if (!normalizedId || !fileId) {
          throw new Error('invalid service file update payload');
        }
        const payload = {
          name: typeof updatedFile?.name === 'string' ? updatedFile.name : '',
          description: typeof updatedFile?.description === 'string' ? updatedFile.description : '',
        };
        if (typeof updatedFile?.path === 'string') {
          payload.path = updatedFile.path;
        }
        return API.put(`/${pathPrefix}/${normalizedId}/file/${fileId}`, payload);
      }
      return API.put(`/${pathPrefix}/${id}/file/${updatedFile._id}`, {
        name: updatedFile.name,
        description: updatedFile.description ?? '',
        path: updatedFile.path,
      });
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: async ({ pathPrefix, id, fileToDelete }) => {
      if (isServicePath(pathPrefix)) {
        const normalizedId = normalizeServiceId(id);
        const fileId = normalizeServiceId(fileToDelete?._id);
        const filename = typeof fileToDelete?.name === 'string' ? fileToDelete.name : '';
        if (!normalizedId || !fileId || !filename) {
          throw new Error('invalid service file delete payload');
        }
        const url = `/${pathPrefix}/${normalizedId}/${fileId}/${encodeURIComponent(filename)}`;
        return API.delete(url);
      }
      const url =
        pathPrefix === 'content-log'
          ? `/${pathPrefix}/${id}/${encodeURIComponent(fileToDelete.name)}`
          : `/${pathPrefix}/${id}/${fileToDelete._id}/${encodeURIComponent(fileToDelete.name)}`;
      return API.delete(encodeURI(url));
    },
  });

  const updateLiveLinkMutation = useMutation({
    mutationFn: async ({ id, updatedLiveLink }) => {
      return API.patch(`/content-log/${id}/live-link`, {
        name: updatedLiveLink.name,
        description: updatedLiveLink.description ?? '',
        path: updatedLiveLink.path,
      });
    },
  });

  const deleteLiveLinkMutation = useMutation({
    mutationFn: async ({ id }) => {
      return API.patch(`/content-log/${id}/live-link`, { isDelete: true });
    },
  });

  const invalidateFuzzySearch = () => {
    if (state?.queryString && state?.category) {
      return queryClient.invalidateQueries({
        queryKey: ['fuzzySearch', type, state.queryString, state.category],
      });
    }
    return Promise.resolve();
  };

  const invalidateMediaInteractionQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['monthlyIssues'] });
    await queryClient.invalidateQueries({ queryKey: ['filteredIssues'] });
    await queryClient.invalidateQueries({ queryKey: ['fuzzySearch', TrackerDataType.MEDIA_INTERACTION] });
    await queryClient.invalidateQueries({ queryKey: ['issueDetail', id] });
  };

  const invalidateServiceQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['monthlyServices'] });
    await queryClient.invalidateQueries({ queryKey: ['filteredServices'] });
    await queryClient.invalidateQueries({ queryKey: ['fuzzySearch', TrackerDataType.SERVICE] });
    await queryClient.invalidateQueries({ queryKey: ['serviceDetail', id] });
  };

  const invalidateEngagementQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['monthlyEngagements'] });
    await queryClient.invalidateQueries({ queryKey: ['filteredEngagements'] });
    await queryClient.invalidateQueries({ queryKey: ['fuzzySearch', TrackerDataType.ENGAGEMENT] });
    await queryClient.invalidateQueries({ queryKey: ['engagementDetail', id] });
  };

  const invalidateContentLogQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['monthlyContentLogs'] });
    await queryClient.invalidateQueries({ queryKey: ['filteredContentLogs'] });
    await queryClient.invalidateQueries({ queryKey: ['fuzzySearch', TrackerDataType.CONTENT_LOG] });
    await queryClient.invalidateQueries({ queryKey: ['contentLogDetail', id] });
  };

  const invalidateUploadQueries = async ({ includeFallbackMonthlyQuery = false } = {}) => {
    if (type === TrackerDataType.SERVICE) {
      await invalidateServiceQueries();
    } else if (type === TrackerDataType.ENGAGEMENT) {
      await invalidateEngagementQueries();
    } else if (type === TrackerDataType.CONTENT_LOG) {
      await invalidateContentLogQueries();
    } else {
      if (includeFallbackMonthlyQuery) {
        await queryClient.invalidateQueries({ queryKey: [monthlyUploadQueryKeysByType[type] ?? ''] });
      }
      await invalidateFuzzySearch();
    }
  };

  const onChange = (colName) => async (e) => {
    if (!e.target.files.length) {
      return;
    }

    const files = Object.values(e.target.files);
    const uploadPathPrefix = uploadPathPrefixesByType[type] ?? 'engagement';

    setFileProgress(0);

    try {
      const response = await uploadFilesMutation.mutateAsync({
        pathPrefix: uploadPathPrefix,
        id,
        files,
        onProgress: (data) => {
          setFileProgress(Math.round((100 * data.loaded) / data.total));
        },
      });

      if (response.status !== 201) {
        alert(response.statusText);
      } else {
        await invalidateUploadQueries({ includeFallbackMonthlyQuery: true });
        dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
      }

      const filesData = response.data.files || null;
      const cellData = cells[colName].concat(filesData);

      onEdit(cellData, colName);
    } catch (err) {
      alert(`post failed - ${err}`);
    }
  };

  const handleDrag = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (colName) => async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!e.dataTransfer.files?.[0] || !isDragActive) {
      setDragActive(false);
      return;
    }

    const files = Object.values(e.dataTransfer.files);
    const uploadPathPrefix = uploadPathPrefixesByType[type] ?? 'service';

    setFileProgress(0);

    try {
      const response = await uploadFilesMutation.mutateAsync({
        pathPrefix: uploadPathPrefix,
        id,
        files,
        onProgress: (data) => {
          setFileProgress(Math.round((100 * data.loaded) / data.total));
        },
      });

      if (response.status !== 201) {
        alert(response.statusText);
      } else {
        await invalidateUploadQueries();
        dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
      }

      const filesData = response.data.files || null;
      const cellData = cells[colName].concat(filesData);

      onEdit(cellData, colName);
    } catch (err) {
      alert(`post failed - ${err}`);
    }
    setDragActive(false);
  };

  const handleSubmit = (callback, dialogValue, handleClose, colName, id, dispatch) => async (event) => {
    event.preventDefault();

    let pathPrefix;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        invalidateQueryKey = 'monthlyEngagements';
        break;
      case TrackerDataType.CONTENT_LOG:
        pathPrefix = 'content-log';
        invalidateQueryKey = 'monthlyContentLogs';
        break;
      default:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
    }

    handleClose();

    try {
      const response = await uploadUrlMutation.mutateAsync({ pathPrefix, id, dialogValue });
      if (response.status !== 201) {
        alert(response.statusText);
      } else {
        if (type === TrackerDataType.MEDIA_INTERACTION) {
          await invalidateMediaInteractionQueries();
        } else if (type === TrackerDataType.SERVICE) {
          await invalidateServiceQueries();
        } else if (type === TrackerDataType.ENGAGEMENT) {
          await invalidateEngagementQueries();
        } else if (type === TrackerDataType.CONTENT_LOG) {
          await invalidateContentLogQueries();
        } else {
          await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
          await invalidateFuzzySearch();
        }
        dispatch(openSnackbar({ severity: 'success', message: `${colName} entry added!` }));
      }
      callback(response, colName);
    } catch (e) {
      console.log('post failed - ', e);
    }
  };

  const handleCommsChange = (response, colName) => {
    const filesData =
      response.data.updatedService?.file ||
      response.data.updatedIssue?.file ||
      response.data.updatedEngagement?.file ||
      response.data.updatedContentLog?.files ||
      null;

    onEdit(filesData, colName);
  };

  const [openForm, setOpenForm] = useState(false);
  const [openSecondaryForm, setOpenSecondaryForm] = useState(false);
  const [openTertiaryForm, setOpenTertiaryForm] = useState(false);

  const handleOpenEditModal = (file, colName) => {
    setCurrentFileToEdit(file);
    setCurrentEditColumn(colName ?? null);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentFileToEdit(null);
    setCurrentEditColumn(null);
  };

  const handleSaveCommsMaterialItem = async (updatedFile) => {
    let pathPrefix;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        invalidateQueryKey = 'monthlyEngagements';
        break;
      case TrackerDataType.CONTENT_LOG:
        pathPrefix = 'content-log';
        invalidateQueryKey = 'monthlyContentLogs';
        break;
      default:
        dispatch(openSnackbar({ severity: 'error', message: 'Unknown data type for saving file.' }));
        return;
    }

    try {
      const response = await updateFileMutation.mutateAsync({ pathPrefix, id, updatedFile });

      if (response.status === 200) {
        if (type === TrackerDataType.MEDIA_INTERACTION) {
          await invalidateMediaInteractionQueries();
        } else if (type === TrackerDataType.SERVICE) {
          await invalidateServiceQueries();
        } else if (type === TrackerDataType.ENGAGEMENT) {
          await invalidateEngagementQueries();
        } else if (type === TrackerDataType.CONTENT_LOG) {
          await invalidateContentLogQueries();
        } else {
          await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
        }
        dispatch(openSnackbar({ severity: 'success', message: 'File details updated!' }));
        const updatedFiles =
          response.data.updatedService?.file ||
          response.data.updatedIssue?.file ||
          response.data.updatedEngagement?.file ||
          response.data.updatedContentLog?.files ||
          [];
        let fallbackFileColName = ContentLogKeys.file;
        if (type === TrackerDataType.SERVICE) {
          fallbackFileColName = ServiceKeys.file;
        } else if (type === TrackerDataType.MEDIA_INTERACTION) {
          fallbackFileColName = IssueKeys.file;
        } else if (type === TrackerDataType.ENGAGEMENT) {
          fallbackFileColName = EngagementKeys.file;
        }

        onEdit(updatedFiles, updatedFile.colName || fallbackFileColName);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to update file: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to update file', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while updating the file.' }));
    }
  };

  const handleDeleteCommsMaterialItem = async (fileToDelete) => {
    let pathPrefix;
    let cellNameToUpdate;
    let invalidateQueryKey;
    switch (type) {
      case TrackerDataType.SERVICE:
        pathPrefix = 'service';
        cellNameToUpdate = ServiceKeys.file;
        invalidateQueryKey = 'monthlyServices';
        break;
      case TrackerDataType.MEDIA_INTERACTION:
        pathPrefix = 'issues';
        cellNameToUpdate = IssueKeys.file;
        invalidateQueryKey = 'monthlyIssues';
        break;
      case TrackerDataType.ENGAGEMENT:
        pathPrefix = 'engagement';
        cellNameToUpdate = EngagementKeys.file;
        invalidateQueryKey = 'monthlyEngagements';
        break;
      case TrackerDataType.CONTENT_LOG:
        pathPrefix = 'content-log';
        cellNameToUpdate = ContentLogKeys.file;
        invalidateQueryKey = 'monthlyContentLogs';
        break;
      default:
        dispatch(openSnackbar({ severity: 'error', message: 'Unknown data type for deleting file.' }));
        return;
    }

    try {
      const response = await deleteFileMutation.mutateAsync({ pathPrefix, id, fileToDelete });

      if (response.status === 204) {
        if (type === TrackerDataType.MEDIA_INTERACTION) {
          await invalidateMediaInteractionQueries();
        } else if (type === TrackerDataType.SERVICE) {
          await invalidateServiceQueries();
        } else if (type === TrackerDataType.ENGAGEMENT) {
          await invalidateEngagementQueries();
        } else if (type === TrackerDataType.CONTENT_LOG) {
          await invalidateContentLogQueries();
        } else {
          await queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
        }
        dispatch(openSnackbar({ severity: 'warning', message: `"${fileToDelete.name}" deleted!` }));
        const currentFiles = cells[cellNameToUpdate] || [];
        const updatedFiles = currentFiles.filter((f) => f._id !== fileToDelete._id);
        onEdit(updatedFiles, cellNameToUpdate);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to delete file: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to delete file', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while deleting the file.' }));
    }
  };

  const handleSaveLiveLinkItem = async (updatedLiveLink) => {
    if (type !== TrackerDataType.CONTENT_LOG) {
      dispatch(openSnackbar({ severity: 'error', message: 'Live Link is only supported for Content Log.' }));
      return;
    }

    try {
      const response = await updateLiveLinkMutation.mutateAsync({ id, updatedLiveLink });
      if (response.status === 200) {
        await invalidateContentLogQueries();
        dispatch(openSnackbar({ severity: 'success', message: 'Live Link updated!' }));
        onEdit(response.data.updatedContentLog?.liveLink ?? null, ContentLogKeys.liveLink);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to update live link: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to update live link', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while updating the live link.' }));
    }
  };

  const handleDeleteLiveLinkItem = async () => {
    if (type !== TrackerDataType.CONTENT_LOG) {
      dispatch(openSnackbar({ severity: 'error', message: 'Live Link is only supported for Content Log.' }));
      return;
    }

    try {
      const response = await deleteLiveLinkMutation.mutateAsync({ id });
      if (response.status === 200) {
        await invalidateContentLogQueries();
        dispatch(openSnackbar({ severity: 'warning', message: 'Live Link deleted!' }));
        onEdit(null, ContentLogKeys.liveLink);
        handleCloseEditModal();
      } else {
        dispatch(openSnackbar({ severity: 'error', message: `Failed to delete live link: ${response.statusText}` }));
      }
    } catch (e) {
      console.error('Failed to delete live link', e);
      dispatch(openSnackbar({ severity: 'error', message: 'An error occurred while deleting the live link.' }));
    }
  };

  return (
    <>
      <ClassNames>
        {({ css }) => {
          const classes = {
            hiddenCellCard: css({
              flex: 1,
              alignSelf: 'flex-start',
              margin: 0,
              textAlign: 'center',
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
            }),
            cardContent: css({
              padding: '0 !important',
              borderRadius: 0,
              border: 'none',
              boxShadow: 'none',
            }),
            cardName: css({
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#e7ebf0',
              padding: '8px 10px',
              fontFamily: '"Open Sans", Arial, sans-serif',
              color: '#000000',
              fontSize: '12px',
              fontWeight: 500,
            }),
            hiddenColsContainer: css({
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-start',
            }),
            collapseWrapperInner: css({
              display: 'flex',
              flexWrap: 'wrap',
              flexDirection: 'row',
              width: '100%',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
            }),
            detailLoadingShell: css({
              width: '100%',
              border: `1px solid ${neutralPalette.borderSubtle}`,
              borderRadius: '6px',
              boxShadow: neutralPalette.shadowSoft,
              background: neutralPalette.white,
              overflow: 'hidden',
            }),
            detailLoadingHeader: css({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minHeight: '40px',
              padding: '8px 12px',
              background: neutralPalette.surfaceRaised,
              borderBottom: `1px solid ${neutralPalette.borderSubtle}`,
              fontFamily: '"Open Sans", Arial, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              color: neutralPalette.textSecondary,
              letterSpacing: '0.01em',
            }),
            detailLoadingGrid: css({
              display: 'grid',
              gridTemplateColumns: `repeat(${getLoadingColumnCount(type)}, minmax(0, 1fr))`,
              gap: '10px',
              padding: '10px',
              background: neutralPalette.white,
            }),
            detailLoadingCard: css({
              border: `1px solid ${neutralPalette.borderSubtle}`,
              borderRadius: '6px',
              padding: '9px',
              background: alpha(neutralPalette.surfacePage, 0.95),
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              minHeight: '74px',
            }),
          };

          const renderHiddenCell = ({ name, editSx, sx }) => {
            const hasLiveLink = Boolean(cells?.[ContentLogKeys.liveLink]);
            const isContentLiveLinkCell = name === ContentLogKeys.liveLink && type === TrackerDataType.CONTENT_LOG;
            const displayName = getHiddenCellDisplayName(name);
            const hiddenCardTitle = getHiddenCardTitle({
              displayName,
              isContentLiveLinkCell,
              userTenantHasContentLogAddOn,
              userTenantHasEngagementAddOn,
            });
            const {
              isFileCell,
              isLinkedLogsHeader,
              isLinkedServiceHeader,
              isMediaStoryLinkCell,
              shouldRenderCommsActionsInDropzone,
              shouldShowFileUpload,
              shouldShowHeaderLinkAction,
              shouldShowLiveLinkUpload,
            } = getHiddenCellFlags({ name, type, hasLiveLink, hiddenCardTitle });
            const commsActionAccentColor = getQueryAdderUiPalette(TrackerDataType.SERVICE).main;
            const dropzoneActions = null;

            const fileUrlDialog = isFileCell ? (
              <UrlDialog
                colName={name}
                type={type}
                id={id}
                isOpen={isUrlFormOpen}
                setIsOpen={setIsUrlFormOpen}
                callback={handleCommsChange}
                handleSubmit={handleSubmit}
              />
            ) : null;

            const hiddenCardActions = getHiddenCardActions({
              isFileCell,
              shouldShowLiveLinkUpload,
              fileActionProps: {
                isMediaStoryLinkCell,
                shouldShowHeaderLinkAction,
                shouldRenderCommsActionsInDropzone,
                shouldShowFileUpload,
                file,
                onChange,
                name,
                setIsUrlFormOpen,
                accentColor: commsActionAccentColor,
              },
              liveLinkActionProps: {
                shouldShowHeaderLinkAction,
                hasLiveLink,
                cells,
                handleOpenEditModal,
                accentColor: commsActionAccentColor,
              },
            });

            return (
              <Box
                key={`${type}-${name}`}
                sx={{
                  minWidth: 0,
                  boxSizing: 'border-box',
                  ...sx,
                }}
              >
                <Card className={classes.hiddenCellCard}>
                  <CardContent className={classes.cardContent}>
                    {fileUrlDialog}
                    <Box className={classes.cardName}>
                      <Box
                        sx={{
                          textAlign: isLinkedLogsHeader || isLinkedServiceHeader ? 'center' : 'left',
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={hiddenCardTitle}
                      >
                        {hiddenCardTitle}
                      </Box>
                      {hiddenCardActions ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>{hiddenCardActions}</Box>
                      ) : null}
                    </Box>
                    <Box sx={{ px: '10px', pt: '6px', pb: '12px', minHeight: '5vh' }}>
                      <FileProgressContext.Provider value={fileProgress}>
                        {isEditMode ? (
                          <CellSelector
                            cells={cells}
                            callBack={onEdit}
                            col={name}
                            dropdownTable={dropdownTable}
                            classes={classes}
                            id={id}
                            type={type}
                            handleDrag={handleDrag}
                            handleDrop={handleDrop(name)}
                            isDragActive={isDragActive}
                            onEditItem={handleOpenEditModal}
                            isExpandedRow={true}
                            commsMaterialActions={dropzoneActions}
                          />
                        ) : (
                          <CellViewer
                            type={type}
                            cells={cells}
                            index={name}
                            callback={onEdit}
                            dropdownTable={dropdownTable}
                            isExpandedView={true}
                            openForm={() => setOpenForm(true)}
                            openSecondaryForm={() => setOpenSecondaryForm(true)}
                            openTertiaryForm={() => setOpenTertiaryForm(true)}
                            classes={classes}
                            id={id}
                            handleDrag={handleDrag}
                            handleDrop={handleDrop(name)}
                            isDragActive={isDragActive}
                            onEditItem={handleOpenEditModal}
                            commsMaterialActions={dropzoneActions}
                            onOpenFullEntryEditor={onOpenFullEntryEditor}
                          />
                        )}
                      </FileProgressContext.Provider>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          };

          return (
            <TableRow style={{ display: open ? 'table-row' : 'none', padding: '4px' }}>
              <TableCell
                colSpan={colSpan}
                style={{
                  padding: '10px',
                  paddingLeft: '20px',
                  paddingRight: '15px',
                  paddingBottom: '16px',
                  borderBottom: 'none',
                }}
              >
                {isLoadingDetail ? (
                  <Box className={classes.detailLoadingShell} role="status" aria-live="polite">
                    <Box className={classes.detailLoadingHeader}>
                      <CircularProgress
                        size={16}
                        thickness={5}
                        sx={{ color: neutralPalette.textHelper, flexShrink: 0 }}
                        aria-hidden="true"
                      />
                      <span>Loading {loadingDetailLabel} details...</span>
                    </Box>
                    <Box className={classes.detailLoadingGrid}>
                      {getLoadingColumnKeys(type).map((loadingColumnKey) => (
                        <Box key={loadingColumnKey} className={classes.detailLoadingCard}>
                          <Skeleton
                            variant="text"
                            width="46%"
                            sx={{ bgcolor: alpha(neutralPalette.borderMuted, 0.62), transform: 'none' }}
                          />
                          <Skeleton
                            variant="rounded"
                            height={12}
                            sx={{ bgcolor: alpha(neutralPalette.borderSubtle, 0.72), transform: 'none' }}
                          />
                          <Skeleton
                            variant="rounded"
                            width="68%"
                            height={12}
                            sx={{ bgcolor: alpha(neutralPalette.borderSubtle, 0.68), transform: 'none' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Collapse in={open} timeout={250} classes={{ wrapperInner: classes.collapseWrapperInner }}>
                    <Box className={classes.hiddenColsContainer}>
                      {hiddenColsMap[type].map((col) => renderHiddenCell(col))}
                    </Box>
                  </Collapse>
                )}
              </TableCell>
            </TableRow>
          );
        }}
      </ClassNames>
      {linkedEventHandler && (
        <QueryAdderDrawer
          openForm={openForm}
          setOpenForm={setOpenForm}
          flush={
            linkedEventHandler?.type === TrackerDataType.ENGAGEMENT ||
            linkedEventHandler?.type === TrackerDataType.CONTENT_LOG
          }
        >
          <QueryAdder
            title={linkedEventHandler?.newItemModalTitle}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenForm}
            eventHandler={linkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      {secondaryLinkedEventHandler && (
        <QueryAdderDrawer
          openForm={openSecondaryForm}
          setOpenForm={setOpenSecondaryForm}
          flush={
            secondaryLinkedEventHandler?.type === TrackerDataType.ENGAGEMENT ||
            secondaryLinkedEventHandler?.type === TrackerDataType.CONTENT_LOG
          }
        >
          <QueryAdder
            title={secondaryLinkedEventHandler?.newItemModalTitle}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenSecondaryForm}
            eventHandler={secondaryLinkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      {tertiaryLinkedEventHandler && (
        <QueryAdderDrawer
          openForm={openTertiaryForm}
          setOpenForm={setOpenTertiaryForm}
          flush={
            tertiaryLinkedEventHandler?.type === TrackerDataType.ENGAGEMENT ||
            tertiaryLinkedEventHandler?.type === TrackerDataType.CONTENT_LOG
          }
        >
          <QueryAdder
            title={'Add a New Content Log'}
            dropdownTable={dropdownTable}
            setOpenForm={setOpenTertiaryForm}
            eventHandler={tertiaryLinkedEventHandler}
            cells={processCellsForAutoComplete(cells, dropdownTable, id)}
            issueId={id}
            linkedType={type}
          />
        </QueryAdderDrawer>
      )}
      {type === TrackerDataType.CONTENT_LOG && currentEditColumn === ContentLogKeys.liveLink ? (
        <EditCollapsedLiveLinkDialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          liveLinkData={currentFileToEdit}
          onSaveItem={handleSaveLiveLinkItem}
          onDeleteItem={handleDeleteLiveLinkItem}
        />
      ) : (
        <EditCommsMaterialDialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          fileData={currentFileToEdit}
          onSaveItem={handleSaveCommsMaterialItem}
          onDeleteItem={handleDeleteCommsMaterialItem}
          type={type}
        />
      )}
    </>
  );
};

TrackerCollapsedRow.propTypes = {
  cells: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  colSpan: PropTypes.number.isRequired,
  dropdownTable: PropTypes.object.isRequired,
  isEditMode: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  type: PropTypes.oneOf([
    TrackerDataType.MEDIA_INTERACTION,
    TrackerDataType.SERVICE,
    TrackerDataType.ENGAGEMENT,
    TrackerDataType.CONTENT_LOG,
  ]).isRequired,
  linkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  secondaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  tertiaryLinkedEventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
    newItemModalTitle: PropTypes.string,
  }),
  onOpenFullEntryEditor: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isLoadingDetail: PropTypes.bool,
};

export default TrackerCollapsedRow;
